import { Flex, Typography, useSafeArea, useScreenSize } from '@banhala/ui-kit';
import Animated, {
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useSharedValue,
  interpolate,
  Extrapolation,
  withTiming,
  useAnimatedReaction,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';
import { ScrollContext } from '../context/ScrollContext';
import { useSetTab, useTabRef } from '../atom/tab.atom';
import { AnimatedList } from './AnimatedList';
import { SectionContainer } from './SectionContainer';
import { CoverImages } from './CoverImages';
import { MarketInfoBanner } from './MarketInfoBanner';
import { MumulLiveProfileSection } from './MumulLive/MumulLiveProfileSection';
import { GoodsInfo } from './GoodsInfo';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ExpectedPurchasePrice } from './ExpectedPurchasePrice';
import { GoodsPromotions } from './GoodsPromotions';
import { CouponListBanner } from './CouponListBanner';
import { DeliveryInfo } from './DeliveryInfo';
import { MarketInfo } from './MarketInfo';
import { BrandInfo } from './BrandInfo';
import type { GoodsDetailContentKeys } from '../types/GoodsDetailContentKeys';
import { useGoodsDetail } from '../queries/useGoodsDetail';
import { CategoryInfo } from './CategoryInfo';
import { ReviewSummary } from './ReviewSummary';
import { GoodsCarousel } from './GoodsCarousel';
import { ContentTab } from './ContentTab';
import type { GoodsDetailTab } from '../types/GoodsDetailTab';
import { GoodsContent } from './GoodsContent';
import { FloatingButtons } from './FloatingButtons';
import { BottomDockedButton } from './BottomDocked';
import { AffiliateFloatingButton } from './AffiliateFloating/AffiliateFloatingButton';
import { useAffiliateFloating } from '../hooks/useAffiliateFloating';
import { useLogViewGoodsDetail } from '../hooks/useLogViewGoodsDetail';
import { useLogClickGoodsDetailTab } from '../hooks/useLogClickGoodsDetailTab';
import { LiveCampaignBanner } from './LiveCampaignBanner';
import { useRelatedLiveCampaign } from '../queries/useRelatedLiveCampaign';
import { useExperimentGroup } from 'shared/src/context/UserContext/useExperimentGroup';
import { useSafeTimeout } from 'shared/src/hooks';
import {
  retainedState,
  useRetainedEffect,
} from 'shared/src/hooks/retainedState';
import { useScrollRestoration } from 'shared/src/hooks/useScrollRestoration';
import { useLogGoodsAnalyticsEvent } from '../hooks/useLogGoodsAnalyticsEvent';
import { useGoodsBasic } from '../queries/useGoodsBasic';
import { Metadata } from 'shared/src/component';
import {
  getMetaDescription,
  getMetaKeywords,
  getMetaTitle,
  getProductMeta,
} from '../utils/goodsMetaData';
import { isAndroid, isIOS, isNotNil, isWeb } from 'shared/src/utils';
import type { ListRenderItem } from 'react-native';
import { useGoodsSno } from '../hooks/useGoodsSno';
import type { FlatList } from 'react-native-gesture-handler';
import { useCheckGoodsRedirectDeeplink } from '../queries/useCheckGoodsRedirectDeeplink';

const BOTTOM_DOCKED_BUTTON_HEIGHT = 56;
const BOTTOM_DOCKED_BUTTON_PADDING = 12 * 2;
const LIVE_CAMPAIGN_BANNER_HEIGHT = 48;

const scrollOffsetSlot = retainedState(0);

// 상단 섹션 — pre-render(usePreRenderParams)로 basic/detail 도착 전에도 즉시 그릴 수 있어
// 항상 표시한다. 모듈 스코프라 not-ready 동안 reference/순서가 안정적이다.
const COMPONENTS_LIST: GoodsDetailContentKeys[] = [
  'cover',
  'promotions',
  'market',
  'info',
  'expected-purchase-price',
  'coupons',
  'delivery-info',
  'brand-info',
  'mumul-live-profile',
  'review-summary',
  'carousel',
  'content-tab',
  'content',
];

const useLiveCampaignCGroupLogging = () => {
  const { data: campaign } = useRelatedLiveCampaign();
  const isCGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['C'],
  });
  const logEvent = useLogGoodsAnalyticsEvent();

  useRetainedEffect(
    'view-shoplive-target-goods-detail',
    () => {
      if (!campaign || !isCGroup) return false;

      logEvent({
        name: 'VIEW_SHOPLIVE_TARGET_GOODS_DETAIL',
        channel: ['braze'],
        analytics: campaign.logging.analytics,
      });
    },
    [campaign, isCGroup, logEvent],
  );
};

export const GoodsDetailContents: React.FC = () => {
  const scrollY = useSharedValue(0);
  const isScrolling = useSharedValue(false);
  const goodsSno = useGoodsSno();

  const tabScrollX = useSharedValue(0);
  const { width, height: screenHeight } = useScreenSize();
  const { bottom } = useSafeArea();
  const setSafeTimeout = useSafeTimeout();
  const listRef = useRef<FlatList<GoodsDetailContentKeys> | null>(null);
  const tabLastScrollYValueRef = useRef<{
    [key in GoodsDetailTab]: number;
  }>({
    INFORMATION: 0,
    REVIEW: 0,
    SIZE: 0,
    INQUIRY: 0,
  });

  const sectionHeightRef = useRef<{ [key: string]: number }>({});
  const stickyItemPositionY = useSharedValue(0);

  const recalcStickyPosition = useCallback(() => {
    const heights = sectionHeightRef.current;
    const nextY = Object.keys(heights)
      .filter(key => key !== 'content' && key !== 'content-tab')
      .reduce((prev, cur) => prev + heights[cur], 0);
    if (stickyItemPositionY.value !== nextY) {
      stickyItemPositionY.value = nextY;
    }
  }, [stickyItemPositionY]);

  const handleChangeHeight = useCallback(
    (key: string, height: number) => {
      if (sectionHeightRef.current[key] === height) return;
      sectionHeightRef.current[key] = height;
      recalcStickyPosition();
    },
    [recalcStickyPosition],
  );

  const { data: goodsBasic } = useGoodsBasic();
  const { data: goodsDetail } = useGoodsDetail();

  const { data: liveCampaign } = useRelatedLiveCampaign();
  const isBGroup = useExperimentGroup({
    sno: 'EXP1558',
    targetGroups: ['B'],
  });
  const showLiveBanner = isNotNil(liveCampaign) && isBGroup;

  const tabRef = useTabRef();
  const setTab = useSetTab();
  const logClickGoodsDetailTab = useLogClickGoodsDetailTab();

  useLiveCampaignCGroupLogging();
  useCheckGoodsRedirectDeeplink();

  const scrollToOffset = useCallback((offset: number, animated: boolean) => {
    listRef.current?.scrollToOffset({ offset, animated });
  }, []);

  const scrollRestore = useScrollRestoration({
    token: scrollOffsetSlot,
    scrollTo: scrollToOffset,
    viewportHeight: screenHeight,
  });

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      scrollY.value = event.contentOffset.y;
      isScrolling.value = true;
    },
    onEndDrag: event => {
      // 손가락을 뗐는데 속도가 거의 없으면(=관성 없음) 즉시 정지로 본다.
      // 속도가 있으면 onMomentum*로 이어지므로 여기서 false 처리하지 않는다.
      if (Math.abs(event.velocity?.y ?? 0) < 0.1) {
        isScrolling.value = false;
        scheduleOnRN(scrollRestore.save, scrollY.value);
      }
    },
    onMomentumBegin: () => {
      isScrolling.value = true;
    },
    onMomentumEnd: () => {
      isScrolling.value = false;
      scheduleOnRN(scrollRestore.save, scrollY.value);
    },
  });

  // sticky overlay 활성 상태 — pointerEvents 제어용.
  // useAnimatedReaction 으로 threshold 교차 시점에만 setState (1프레임 단위 X)
  const [isStickyActive, setIsStickyActive] = useState(false);
  useAnimatedReaction(
    () =>
      stickyItemPositionY.value > 0 &&
      scrollY.value >= stickyItemPositionY.value,
    (current, previous) => {
      if (current !== previous) {
        scheduleOnRN(setIsStickyActive, current);
      }
    },
    [scrollY, stickyItemPositionY],
  );

  const stickyOverlayStyle = useAnimatedStyle(() => {
    const active =
      stickyItemPositionY.value > 0 &&
      scrollY.value >= stickyItemPositionY.value;
    return {
      opacity: active ? 1 : 0,
    };
  });

  const marginBottom = useMemo(() => {
    const DOCKED_PRIMARY_BUTTON_HEIGHT =
      BOTTOM_DOCKED_BUTTON_HEIGHT + BOTTOM_DOCKED_BUTTON_PADDING;
    return bottom + DOCKED_PRIMARY_BUTTON_HEIGHT;
  }, [bottom]);

  const scrollToStickyView = useCallback(() => {
    listRef.current?.scrollToOffset({
      offset: stickyItemPositionY.value,
      animated: true,
    });
  }, [stickyItemPositionY]);

  const changeContentTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      setTab(nextTab);
      const tabList: GoodsDetailTab[] = [
        'INFORMATION',
        'REVIEW',
        'SIZE',
        'INQUIRY',
      ];
      if (isIOS) {
        tabScrollX.value = withTiming(
          tabList.findIndex(t => t === nextTab),
          { duration: 200 },
        );
      }
    },
    [setTab, tabScrollX],
  );

  const onChangeTab = useCallback(
    (nextTab: GoodsDetailTab) => {
      const prevTab = tabRef.current;
      if (prevTab !== nextTab) {
        logClickGoodsDetailTab(nextTab);
        const currentScrollY = scrollY.value;
        //마지막 스크롤 저장
        tabLastScrollYValueRef.current = {
          ...tabLastScrollYValueRef.current,
          [prevTab]: currentScrollY,
        };

        if (currentScrollY > stickyItemPositionY.value) {
          setSafeTimeout(() => {
            scrollToOffset(
              Math.max(
                tabLastScrollYValueRef.current[nextTab],
                stickyItemPositionY.value,
              ),
              false,
            );
          }, 1);
        }
      }
      setTab(nextTab);
    },
    [
      tabRef,
      setTab,
      logClickGoodsDetailTab,
      scrollY,
      scrollToOffset,
      stickyItemPositionY,
      setSafeTimeout,
    ],
  );

  useLogViewGoodsDetail();

  const { floatingText, showFloating, onViewFloating, onClickFloating } =
    useAffiliateFloating();

  const listStyle = useMemo(() => ({ flex: 1, marginBottom }), [marginBottom]);

  const listContentContainerStyle = useMemo(
    () =>
      showLiveBanner ? { paddingTop: LIVE_CAMPAIGN_BANNER_HEIGHT } : undefined,
    [showLiveBanner],
  );

  const renderItem = useCallback<ListRenderItem<GoodsDetailContentKeys>>(
    ({ item }) => {
      if (item === 'cover') {
        return (
          <SectionContainer
            height={width}
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CoverImages />
          </SectionContainer>
        );
      }

      if (item === 'promotions') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsPromotions />
          </SectionContainer>
        );
      }

      if (item === 'market') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MarketInfoBanner />
          </SectionContainer>
        );
      }

      if (item === 'info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsInfo />
          </SectionContainer>
        );
      }

      if (item === 'expected-purchase-price') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ExpectedPurchasePrice />
          </SectionContainer>
        );
      }

      if (item === 'coupons') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CouponListBanner />
          </SectionContainer>
        );
      }

      if (item === 'delivery-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <DeliveryInfo />
          </SectionContainer>
        );
      }

      if (item === 'mumul-live-profile') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MumulLiveProfileSection />
          </SectionContainer>
        );
      }

      if (item === 'market-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <MarketInfo />
          </SectionContainer>
        );
      }
      if (item === 'brand-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <BrandInfo />
          </SectionContainer>
        );
      }
      if (item === 'review-summary') {
        if (!goodsDetail?.component_visibility.review_summary) return null;
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ReviewSummary />
          </SectionContainer>
        );
      }

      if (item === 'category-info') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <CategoryInfo />
          </SectionContainer>
        );
      }

      if (item === 'carousel') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsCarousel />
          </SectionContainer>
        );
      }

      if (item === 'content-tab') {
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <ContentTab />
          </SectionContainer>
        );
      }

      if (item === 'content') {
        // if (!isAndroid && (isNil(goodsBasic) || isNil(goodsDetail))) {
        //   return null;
        // }
        return (
          <SectionContainer
            sectionKey={item}
            onChangeHeight={handleChangeHeight}
          >
            <GoodsContent />
          </SectionContainer>
        );
      }

      return (
        <Flex height={300} backgroundColor={'white'}>
          <Typography>{item}</Typography>
        </Flex>
      );
    },
    [width, handleChangeHeight, goodsDetail],
  );

  const bannerAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateY: interpolate(
            scrollY.value,
            [0, LIVE_CAMPAIGN_BANNER_HEIGHT],
            [0, -LIVE_CAMPAIGN_BANNER_HEIGHT],
            Extrapolation.CLAMP,
          ),
        },
      ],
    };
  });

  const scrollContextValue = useMemo(
    () => ({
      listRef,
      scrollY,
      isScrolling,
      tabScrollX,
      scrollToStickyView,
      changeContentTab,
      onChangeTab,
    }),
    [
      scrollToStickyView,
      changeContentTab,
      onChangeTab,
      listRef,
      scrollY,
      isScrolling,
      tabScrollX,
    ],
  );

  const stickyOverlayContainerStyle = useMemo(
    () => ({
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
    }),
    [],
  );

  return (
    <ScrollContext value={scrollContextValue}>
      {isNotNil(goodsBasic) && (
        <Metadata
          title={getMetaTitle(goodsBasic)}
          imgSrc={goodsBasic?.cover_images[0] ?? ''}
          path={`/goods/${goodsBasic.sno}`}
          description={getMetaDescription(goodsBasic)}
          keywords={getMetaKeywords(goodsBasic)}
          productMeta={getProductMeta(goodsBasic)}
          isCanonical
        />
      )}
      {showLiveBanner && (
        <Animated.View
          style={[
            {
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1,
            },
            bannerAnimatedStyle,
          ]}
        >
          <LiveCampaignBanner />
        </Animated.View>
      )}
      <AnimatedList
        ref={listRef}
        {...(isWeb ? { nativeID: 'goods-detail-scroll' } : {})}
        style={[listStyle, scrollRestore.restoring ? { opacity: 0 } : null]}
        contentContainerStyle={[
          listContentContainerStyle,
          isWeb && { maxWidth: width, alignSelf: 'center' },
        ]}
        removeClippedSubviews={isAndroid}
        windowSize={isAndroid ? 5 : undefined}
        onScroll={scrollHandler}
        onContentSizeChange={scrollRestore.onContentSizeChange}
        contentInsetAdjustmentBehavior={'never'}
        data={COMPONENTS_LIST}
        renderItem={renderItem}
        scrollEventThrottle={1}
      />
      {/* Sticky overlay — content-tab의 자연 위치를 지나면 fade in.
          RN의 stickyHeaderIndices를 쓰지 않고 자체 구현하여
          ScrollViewStickyHeader의 cascading setState 회피. */}
      <Animated.View
        pointerEvents={isStickyActive ? 'auto' : 'none'}
        style={[stickyOverlayContainerStyle, stickyOverlayStyle]}
      >
        <ContentTab />
      </Animated.View>
      <Flex
        position={'absolute'}
        left={0}
        right={0}
        bottom={0}
        alignItems={'center'}
      >
        <Flex width={width}>
          {/* web에서 해당 key값을 할당 하지 않으면 unmount가 되지 않고 단순히 re-render가됨.
          이전 상품에서 선택했던 옵션을 그대로 보여줍니다.
          key값을 추가해서 새로 받아 올 수 있도록 합니다.
          */}
          <BottomDockedButton key={goodsSno.toString()} />
        </Flex>
      </Flex>
      <FloatingButtons
        bottomOffset={marginBottom}
        scrollToYPosition={isAndroid ? stickyItemPositionY.value : 0}
      />
      {showFloating && (
        <AffiliateFloatingButton
          text={floatingText}
          marginBottom={marginBottom}
          onPress={onClickFloating}
          onView={onViewFloating}
        />
      )}
    </ScrollContext>
  );
};
